/**
 * Operator Support — the home of impersonation. An operator helps a hotel
 * WITHOUT unsafe database or provider access: they pick a partner + hotel, then
 * start a short-lived, fully-audited session as one of that hotel's members.
 *
 * This route REUSES the proven `ImpersonateLauncher` (member list + reason +
 * ticket + duration + new-tab handoff) rather than re-implementing the flow; the
 * page's job is to scope it (which tenant / which hotel) and to surround it with
 * honest support context. The backend re-validates everything and is the
 * authority on permission, justification and destructive-action limits.
 */

import { useOperatorHotels, useOperatorTenants } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { Card, cn, EmptyState, Label, PageHeader } from '@hostesa/ui';
import { LifeBuoy, ShieldCheck } from 'lucide-react';
import { useId, useState } from 'react';
import { ImpersonateLauncher } from '../oversight/impersonate-launcher';

const SELECT = cn(
  'h-9 w-full rounded-md border border-[var(--color-input)] bg-[var(--color-alabaster)] px-3',
  'font-[var(--font-text)] text-sm text-[var(--color-charcoal)] transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:border-[var(--color-warm-stone)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
);
const META = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';

export function SupportPage() {
  const { t } = useT('backoffice');
  const tenantId2 = useId();
  const hotelId2 = useId();

  const [tenantId, setTenantId] = useState<string>('');
  const [hotelId, setHotelId] = useState<string>('');

  const tenants = useOperatorTenants();
  // Hotels for the chosen tenant only — the launcher needs both ids to scope a session.
  const hotels = useOperatorHotels(tenantId ? { tenantId } : undefined);

  const tenantRows = tenants.data?.items ?? [];
  const hotelRows = hotels.data?.items ?? [];
  const ready = Boolean(tenantId && hotelId);

  return (
    <section data-testid="support-page" className="mx-auto w-full max-w-6xl px-8 py-8">
      <PageHeader
        eyebrow={t('navGroups.support')}
        title={t('support.title')}
        description={t('support.description')}
      />

      {/* Self-acting / safety notice — operators must understand what a session is. */}
      <div
        data-testid="support-safety-notice"
        role="note"
        className="mb-6 flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-accent-wash)] px-4 py-3"
      >
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[var(--color-accent-deep)]" />
        <div className="text-sm text-[var(--color-charcoal)]">
          <strong className="font-[var(--font-display)]">{t('support.safety.label')}</strong>
          <p className="mt-1 text-[var(--color-stone-mid)]">{t('support.safety.body')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* Start a session: scope to tenant + hotel, then the launcher takes over. */}
        <Card data-testid="support-start-card" className="flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-0.5">
            <h2 className="font-[var(--font-display)] text-sm text-[var(--color-charcoal)]">
              {t('support.start.title')}
            </h2>
            <p className={META}>{t('support.start.description')}</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor={tenantId2}>{t('support.start.tenantLabel')}</Label>
              <select
                id={tenantId2}
                data-testid="support-tenant-select"
                className={SELECT}
                value={tenantId}
                disabled={tenants.isLoading}
                onChange={(e) => {
                  setTenantId(e.target.value);
                  setHotelId(''); // hotel list depends on the tenant — reset on change
                }}
              >
                <option value="">{t('support.start.tenantPlaceholder')}</option>
                {tenantRows.map((tn) => (
                  <option key={tn.id} value={tn.id}>
                    {tn.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor={hotelId2}>{t('support.start.hotelLabel')}</Label>
              <select
                id={hotelId2}
                data-testid="support-hotel-select"
                className={SELECT}
                value={hotelId}
                disabled={!tenantId || hotels.isLoading}
                onChange={(e) => setHotelId(e.target.value)}
              >
                <option value="">{t('support.start.hotelPlaceholder')}</option>
                {hotelRows.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {ready ? (
            <ImpersonateLauncher tenantId={tenantId} hotelId={hotelId} />
          ) : (
            <p data-testid="support-scope-hint" className={META}>
              {t('support.start.selectScopeHint')}
            </p>
          )}
        </Card>

        {/* Support context — honest empty state until the operator support-context
            endpoint exists (documented gap; do not fake it). */}
        <Card data-testid="support-context-card" className="flex flex-col gap-3 p-5">
          <div className="flex flex-col gap-0.5">
            <h2 className="font-[var(--font-display)] text-sm text-[var(--color-charcoal)]">
              {t('support.context.title')}
            </h2>
            <p className={META}>{t('support.context.description')}</p>
          </div>
          <EmptyState
            data-testid="support-context-empty"
            icon={<LifeBuoy />}
            title={t('support.context.emptyTitle')}
            description={t('support.context.emptyDescription')}
          />
        </Card>
      </div>
    </section>
  );
}
